import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    FormControlLabel,
    IconButton,
    Chip,
    useMediaQuery,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { analyticsApi } from '../api/analyticsApi';
import { formatCurrency as formatCurrencyValue } from '../utils/currency';

const RevenueAnalysis = ({ affiliateId }) => {
    const [revenueData, setRevenueData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [includeVAT, setIncludeVAT] = useState(false);
    const [monthOffset, setMonthOffset] = useState(0);
    const [selectedPeriod, setSelectedPeriod] = useState('current'); // 'previous', 'current', 'next'

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (!affiliateId || Number.isNaN(Number(affiliateId))) {
            setLoading(false);
            return;
        }
        loadRevenueData();
    }, [affiliateId, monthOffset]);

    const loadRevenueData = async () => {
        if (!affiliateId || Number.isNaN(Number(affiliateId))) {
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const result = await analyticsApi.getRevenueAnalysis(token, affiliateId, monthOffset);
            console.log('Backend response:', result); // Debug log
            setRevenueData(result);
        } catch (error) {
            console.error('Error loading revenue analysis:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatMoney = (amount) => {
        let finalAmount = amount;
        if (!includeVAT && revenueData?.vatRate) {
            // Remove VAT (amounts in DB include VAT)
            finalAmount = amount / (1 + revenueData.vatRate / 100);
        }

        return formatCurrencyValue(Math.round(finalAmount || 0), revenueData?.paymentCurrency || 'EUR');
    };

    const formatCount = (count) => {
        return count || 0;
    };

    const navigateMonth = (direction) => {
        setMonthOffset(prev => prev + direction);
    };

    const getCurrentPeriodData = () => {
        if (!revenueData) return null;

        switch(selectedPeriod) {
            case 'previous':
                return revenueData.previousMonth;
            case 'next':
                return revenueData.nextMonth;
            default:
                return revenueData.currentMonth;
        }
    };

    const getMobileTableData = () => {
        if (!revenueData) return [];

        const currentData = getCurrentPeriodData();
        if (!currentData) return [];

        const tableData = [
            {
                category: 'Total Purchases',
                value: currentData.totalPurchases,
                isCount: true,
                highlight: true
            },
            {
                category: 'Contract Clients',
                value: `${currentData.contractClients.count}x (${formatMoney(currentData.contractClients.revenue)})`,
                isCount: false,
                isCustomFormat: true
            },
            {
                category: 'Children Contracts',
                value: `${currentData.childrenContracts?.count || 0}x (${formatMoney(currentData.childrenContracts?.revenue || 0)})`,
                isCount: false,
                isChildrenContracts: true,
                isCustomFormat: true
            },
            {
                category: 'Contract Revenue',
                value: (currentData.contractClients.revenue || 0) + (currentData.childrenContracts?.revenue || 0),
                isCount: false,
                isRevenueBold: true
            },
            {
                category: 'Plan Packages Count',
                value: currentData.oneTimePackages.count,
                isCount: true
            }
        ];

        // Add individual package data for mobile - combined format
        if (currentData.oneTimePackages.plansByName && Array.isArray(currentData.oneTimePackages.plansByName)) {
            currentData.oneTimePackages.plansByName.forEach(plan => {
                tableData.push({
                    category: plan.planName,
                    value: `${plan.count}x (${formatMoney(plan.revenue)})`,
                    isCount: false,
                    isPackage: true,
                    isCustomFormat: true
                });
            });
        }

        // Add totals for mobile
        tableData.push(
            {
                category: 'Plan Revenue',
                value: currentData.oneTimePackages.revenue,
                isCount: false,
                isRevenueBold: true
            },
            {
                category: 'TOTAL REVENUE',
                value: currentData.totalRevenue,
                isCount: false,
                isTotal: true
            }
        );

        return tableData;
    };

    if (loading) {
        return (
            <Paper elevation={2} sx={{ p: isMobile ? 2 : 3, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Paper>
        );
    }

    if (!revenueData) {
        return (
            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography>No data found</Typography>
            </Paper>
        );
    }

    // Prepare table data for DESKTOP view
    const tableData = [
        {
            category: 'Total Purchases',
            previous: revenueData.previousMonth.totalPurchases,
            current: revenueData.currentMonth.totalPurchases,
            next: revenueData.nextMonth.totalPurchases,
            isCount: true,
            highlight: true
        },
        {
            category: 'Contract Clients',
            previous: `${revenueData.previousMonth.contractClients.count}x (${formatMoney(revenueData.previousMonth.contractClients.revenue)})`,
            current: `${revenueData.currentMonth.contractClients.count}x (${formatMoney(revenueData.currentMonth.contractClients.revenue)})`,
            next: `${revenueData.nextMonth.contractClients.count}x (${formatMoney(revenueData.nextMonth.contractClients.revenue)})`,
            isCount: false,
            isCustomFormat: true
        },
        {
            category: 'Children Contracts',
            previous: `${revenueData.previousMonth.childrenContracts?.count || 0}x (${formatMoney(revenueData.previousMonth.childrenContracts?.revenue || 0)})`,
            current: `${revenueData.currentMonth.childrenContracts?.count || 0}x (${formatMoney(revenueData.currentMonth.childrenContracts?.revenue || 0)})`,
            next: `${revenueData.nextMonth.childrenContracts?.count || 0}x (${formatMoney(revenueData.nextMonth.childrenContracts?.revenue || 0)})`,
            isCount: false,
            isChildrenContracts: true,
            isCustomFormat: true
        },
        {
            category: 'Contract Revenue',
            previous: (revenueData.previousMonth.contractClients.revenue || 0) + (revenueData.previousMonth.childrenContracts?.revenue || 0),
            current: (revenueData.currentMonth.contractClients.revenue || 0) + (revenueData.currentMonth.childrenContracts?.revenue || 0),
            next: (revenueData.nextMonth.contractClients.revenue || 0) + (revenueData.nextMonth.childrenContracts?.revenue || 0),
            isCount: false,
            isRevenueBold: true
        },
        {
            category: 'Plan Packages Count',
            previous: revenueData.previousMonth.oneTimePackages.count,
            current: revenueData.currentMonth.oneTimePackages.count,
            next: revenueData.nextMonth.oneTimePackages.count,
            isCount: true
        }
    ];

    // Get all unique package names across all periods
    const allPackageNames = new Set();
    [revenueData.previousMonth, revenueData.currentMonth, revenueData.nextMonth].forEach(month => {
        if (month.oneTimePackages.plansByName && Array.isArray(month.oneTimePackages.plansByName)) {
            month.oneTimePackages.plansByName.forEach(plan => {
                allPackageNames.add(plan.planName);
            });
        }
    });

    console.log('All package names found:', Array.from(allPackageNames)); // Debug log

    // Add individual package data - ONLY combined format (count + revenue in one line)
    Array.from(allPackageNames).forEach(packageName => {
        const getPlanData = (monthData, planName) => {
            if (!monthData.oneTimePackages.plansByName || !Array.isArray(monthData.oneTimePackages.plansByName)) {
                return { count: 0, revenue: 0 };
            }
            const plan = monthData.oneTimePackages.plansByName.find(p => p.planName === planName);
            return plan ? { count: plan.count, revenue: plan.revenue } : { count: 0, revenue: 0 };
        };

        const prevData = getPlanData(revenueData.previousMonth, packageName);
        const currData = getPlanData(revenueData.currentMonth, packageName);
        const nextData = getPlanData(revenueData.nextMonth, packageName);

        tableData.push({
            category: packageName,
            previous: `${prevData.count}x (${formatMoney(prevData.revenue)})`,
            current: `${currData.count}x (${formatMoney(currData.revenue)})`,
            next: `${nextData.count}x (${formatMoney(nextData.revenue)})`,
            isCount: false,
            isPackage: true,
            isCustomFormat: true
        });
    });

    // Add totals
    tableData.push(
        {
            category: 'Plan Revenue',
            previous: revenueData.previousMonth.oneTimePackages.revenue,
            current: revenueData.currentMonth.oneTimePackages.revenue,
            next: revenueData.nextMonth.oneTimePackages.revenue,
            isCount: false,
            isRevenueBold: true
        },
        {
            category: 'TOTAL REVENUE',
            previous: revenueData.previousMonth.totalRevenue,
            current: revenueData.currentMonth.totalRevenue,
            next: revenueData.nextMonth.totalRevenue,
            isCount: false,
            isTotal: true
        }
    );

    return (
        <Paper elevation={2} sx={{ p: isMobile ? 2 : 3 }}>
            {/* Header with navigation */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                    <IconButton onClick={() => navigateMonth(-1)} size={isMobile ? "small" : "medium"}>
                        <ArrowBackIosIcon />
                    </IconButton>
                    <Typography variant={isMobile ? "h5" : "h4"} component="h2" sx={{ fontWeight: 'bold' }}>
                        Revenue Analysis
                    </Typography>
                    <IconButton onClick={() => navigateMonth(1)} size={isMobile ? "small" : "medium"}>
                        <ArrowForwardIosIcon />
                    </IconButton>
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={includeVAT}
                            onChange={(e) => setIncludeVAT(e.target.checked)}
                            color="primary"
                            size={isMobile ? "small" : "medium"}
                        />
                    }
                    label={
                        <Typography variant={isMobile ? "caption" : "body2"}>
                            {`${includeVAT ? 'With' : 'Without'} VAT (${revenueData?.vatRate || 0}%)`}
                        </Typography>
                    }
                />
            </Box>

            {/* Mobile period selector */}
            {isMobile && revenueData && (
                <Box mb={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Select Period</InputLabel>
                        <Select
                            value={selectedPeriod}
                            label="Select Period"
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                        >
                            <MenuItem value="previous">{revenueData.previousMonth.label}</MenuItem>
                            <MenuItem value="current">{revenueData.currentMonth.label}</MenuItem>
                            <MenuItem value="next">{revenueData.nextMonth.label}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Desktop view - Excel-style table */}
            {!isMobile ? (
                <TableContainer component={Paper} elevation={1}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    minWidth: 200,
                                    borderRight: '2px solid #e0e0e0',
                                    bgcolor: 'grey.200'
                                }}>
                                    Category
                                </TableCell>
                                <TableCell align="center" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    minWidth: 150,
                                    borderRight: '1px solid #e0e0e0'
                                }}>
                                    {revenueData.previousMonth.label}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    minWidth: 150,
                                    borderRight: '1px solid #e0e0e0',
                                    bgcolor: 'primary.50'
                                }}>
                                    {revenueData.currentMonth.label}
                                </TableCell>
                                <TableCell align="center" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    minWidth: 150
                                }}>
                                    {revenueData.nextMonth.label}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                                        ...(row.highlight && { bgcolor: 'success.50' }),
                                        ...(row.isTotal && { bgcolor: 'warning.100' }),
                                        ...(row.isRevenueBold && { bgcolor: 'info.50' }),
                                        ...(row.isChildrenContracts && { bgcolor: 'secondary.50' }),
                                        border: row.isTotal ? '2px solid #ff9800' :
                                            (row.isRevenueBold ? '2px solid #2196f3' :
                                                (row.isChildrenContracts ? '1px solid #9c27b0' : 'none'))
                                    }}
                                >
                                    <TableCell sx={{
                                        fontWeight: row.isTotal ? 'bold' : (row.highlight || row.isRevenueBold ? 'bold' : 'normal'),
                                        borderRight: '2px solid #e0e0e0',
                                        bgcolor: row.isTotal ? 'warning.200' :
                                            (row.highlight ? 'success.100' :
                                                (row.isRevenueBold ? 'info.100' :
                                                    (row.isChildrenContracts ? 'secondary.100' : 'inherit'))),
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '1rem' : '0.875rem',
                                        ...(row.isPackage && { pl: 3, fontStyle: 'italic' })
                                    }}>
                                        {row.category}
                                        {row.highlight && (
                                            <Chip
                                                label="KEY METRIC"
                                                size="small"
                                                color="success"
                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        {row.isRevenueBold && (
                                            <Chip
                                                label="REVENUE"
                                                size="small"
                                                color="info"
                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        {row.isChildrenContracts && (
                                            <Chip
                                                label="CHILDREN"
                                                size="small"
                                                color="secondary"
                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{
                                        borderRight: '1px solid #e0e0e0',
                                        fontWeight: (row.isTotal || row.isRevenueBold) ? 'bold' : 'normal',
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '1rem' : '0.875rem'
                                    }}>
                                        {row.isCustomFormat ? row.previous : (row.isCount ? formatCount(row.previous) : formatMoney(row.previous))}
                                    </TableCell>
                                    <TableCell align="center" sx={{
                                        borderRight: '1px solid #e0e0e0',
                                        bgcolor: 'primary.50',
                                        fontWeight: (row.isTotal || row.isRevenueBold) ? 'bold' : 'normal',
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '1rem' : '0.875rem'
                                    }}>
                                        {row.isCustomFormat ? row.current : (row.isCount ? formatCount(row.current) : formatMoney(row.current))}
                                    </TableCell>
                                    <TableCell align="center" sx={{
                                        fontWeight: (row.isTotal || row.isRevenueBold) ? 'bold' : 'normal',
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '1rem' : '0.875rem'
                                    }}>
                                        {row.isCustomFormat ? row.next : (row.isCount ? formatCount(row.next) : formatMoney(row.next))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                /* Mobile view - Single column table */
                <TableContainer component={Paper} elevation={1}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    bgcolor: 'grey.200'
                                }}>
                                    Category
                                </TableCell>
                                <TableCell align="right" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    bgcolor: 'primary.50'
                                }}>
                                    {getCurrentPeriodData()?.label}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getMobileTableData().map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                                        ...(row.highlight && { bgcolor: 'success.50' }),
                                        ...(row.isTotal && { bgcolor: 'warning.100' }),
                                        ...(row.isRevenueBold && { bgcolor: 'info.50' }),
                                        ...(row.isChildrenContracts && { bgcolor: 'secondary.50' }),
                                        border: row.isTotal ? '2px solid #ff9800' :
                                            (row.isRevenueBold ? '2px solid #2196f3' :
                                                (row.isChildrenContracts ? '1px solid #9c27b0' : 'none'))
                                    }}
                                >
                                    <TableCell sx={{
                                        fontWeight: row.isTotal ? 'bold' : (row.highlight || row.isRevenueBold ? 'bold' : 'normal'),
                                        bgcolor: row.isTotal ? 'warning.200' :
                                            (row.highlight ? 'success.100' :
                                                (row.isRevenueBold ? 'info.100' :
                                                    (row.isChildrenContracts ? 'secondary.100' : 'inherit'))),
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '0.9rem' : '0.8rem',
                                        ...(row.isPackage && { pl: 2, fontStyle: 'italic' }),
                                        py: 1
                                    }}>
                                        {row.category}
                                        {row.highlight && (
                                            <Chip
                                                label="KEY"
                                                size="small"
                                                color="success"
                                                sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                                            />
                                        )}
                                        {row.isRevenueBold && (
                                            <Chip
                                                label="REV"
                                                size="small"
                                                color="info"
                                                sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                                            />
                                        )}
                                        {row.isChildrenContracts && (
                                            <Chip
                                                label="CHILD"
                                                size="small"
                                                color="secondary"
                                                sx={{ ml: 1, fontSize: '0.6rem', height: 16 }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" sx={{
                                        fontWeight: (row.isTotal || row.isRevenueBold) ? 'bold' : 'normal',
                                        fontSize: (row.isTotal || row.isRevenueBold) ? '0.9rem' : '0.8rem',
                                        py: 1
                                    }}>
                                        {row.isCustomFormat ? row.value : (row.isCount ? formatCount(row.value) : formatMoney(row.value))}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* VAT notice */}
            {!includeVAT && revenueData?.vatRate > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="info.dark">
                        * All amounts are displayed without {revenueData.vatRate}% VAT.
                        Database stores amounts including VAT.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default RevenueAnalysis;